// SPDX-License-Identifier: GPL-3.0-or-later
// The key list of the Settings dialog (THEME.md 11.1). It binds the pure rules of keybinds.ts to
// the page: one row for each command, a Change button, a capture of the next key, and a reset.
// While a key is captured, the key goes to the rebinding, not to the game or to the dialog.

import {
  BINDABLE_COMMANDS,
  type BindableCommand,
  type Bindings,
  bindingsToMap,
  COMMAND_LABELS,
  keysSummary,
  keysText,
  loadBindings,
  rebind,
  resetBindings,
  type StorageLike,
  saveBindings,
} from "./keybinds.ts";

/** What the page gives the key list. */
export interface KeySettingsDeps {
  readonly doc: Document;
  /** The list that holds one row for each command. */
  readonly list: HTMLElement;
  /** The line under the list. It tells what the last key change did. */
  readonly note: HTMLElement;
  /** The key summary of the start dialog. */
  readonly summary: HTMLElement;
  readonly dialog: HTMLDialogElement;
  readonly resetButton: HTMLButtonElement;
  readonly store: StorageLike | null;
  /** The bindings changed. The page lifts every held key: a key that is down must not stay down. */
  readonly onChange: () => void;
}

/** The key list. */
export interface KeySettings {
  /** The command of a key code, or null. The keyboard controller uses it. */
  commandFor(code: string): BindableCommand | null;
}

/** The state of the key list. */
interface State {
  bindings: Bindings;
  keyMap: ReadonlyMap<string, BindableCommand>;
  /** The command that waits for a key, or null. */
  capturing: BindableCommand | null;
  /** Set when Escape cancelled a capture. The dialog must stay open for that one Escape. */
  suppressClose: boolean;
}

interface Ctx {
  readonly deps: KeySettingsDeps;
  readonly state: State;
}

function applyBindings(ctx: Ctx, next: Bindings): void {
  ctx.state.bindings = next;
  ctx.state.keyMap = bindingsToMap(next);
  ctx.deps.onChange();
}

function beginCapture(ctx: Ctx, command: BindableCommand): void {
  ctx.state.capturing = command;
  ctx.deps.note.textContent = `Press a key for "${COMMAND_LABELS[command]}". Escape cancels.`;
  renderKeys(ctx, command);
}

/** One row: the command, its keys, and a Change button. */
function buildRow(
  ctx: Ctx,
  command: BindableCommand,
): { readonly li: HTMLElement; readonly button: HTMLButtonElement } {
  const { doc } = ctx.deps;
  const waiting = ctx.state.capturing === command;
  const li = doc.createElement("li");
  const label = doc.createElement("span");
  const keys = waiting ? "press a key" : keysText(ctx.state.bindings, command);
  label.textContent = `${COMMAND_LABELS[command]}: ${keys}`;
  const button = doc.createElement("button");
  button.type = "button";
  button.className = "btn";
  button.textContent = waiting ? "Press a key" : "Change";
  button.setAttribute("aria-label", `Change the key for ${COMMAND_LABELS[command]}`);
  button.addEventListener("click", () => beginCapture(ctx, command));
  li.append(label, " ", button);
  return { li, button };
}

/** Draw the list. The button of `focus` gets the focus. */
function renderKeys(ctx: Ctx, focus: BindableCommand | null = null): void {
  ctx.deps.summary.textContent = keysSummary(ctx.state.bindings);
  const rows = BINDABLE_COMMANDS.map((command) => ({ command, ...buildRow(ctx, command) }));
  ctx.deps.list.replaceChildren(...rows.map((row) => row.li));
  rows.find((row) => row.command === focus)?.button.focus();
}

/** End a capture with this key code. The result is the text for the note. */
function endCapture(ctx: Ctx, command: BindableCommand, code: string): string {
  if (code === "Escape") {
    ctx.state.suppressClose = true;
    return "Key change cancelled.";
  }
  const result = rebind(ctx.state.bindings, command, code);
  if (!result.ok) return result.message;
  applyBindings(ctx, result.bindings);
  saveBindings(ctx.deps.store, ctx.state.bindings);
  return `${COMMAND_LABELS[command]}: ${keysText(ctx.state.bindings, command)}.`;
}

/** The capture phase handler of keydown. It takes the key while a capture waits. */
function onCaptureKey(ctx: Ctx, e: KeyboardEvent): void {
  const command = ctx.state.capturing;
  if (command === null) {
    ctx.state.suppressClose = false;
    return;
  }
  e.preventDefault();
  e.stopPropagation();
  if (e.repeat) return;
  ctx.state.capturing = null;
  ctx.deps.note.textContent = endCapture(ctx, command, e.code);
  renderKeys(ctx, command);
}

/** The dialog closes: drop a capture that waits and clear the note. */
function onDialogClose(ctx: Ctx): void {
  ctx.state.capturing = null;
  ctx.deps.note.textContent = "";
  renderKeys(ctx);
}

/** Escape that cancelled a capture must not close the dialog. */
function onDialogCancel(ctx: Ctx, e: Event): void {
  if (!ctx.state.suppressClose) return;
  ctx.state.suppressClose = false;
  e.preventDefault();
}

function onReset(ctx: Ctx): void {
  ctx.state.capturing = null;
  applyBindings(ctx, resetBindings(ctx.deps.store));
  ctx.deps.note.textContent = "Keys are back to the defaults.";
  renderKeys(ctx);
}

/** Bind the key list to the page and draw it. */
export function bindKeySettings(deps: KeySettingsDeps): KeySettings {
  const bindings = loadBindings(deps.store);
  const ctx: Ctx = {
    deps,
    state: { bindings, keyMap: bindingsToMap(bindings), capturing: null, suppressClose: false },
  };
  // Capture phase: the key goes to the rebinding, not to the game or to the dialog.
  deps.doc.addEventListener("keydown", (e) => onCaptureKey(ctx, e), { capture: true });
  deps.dialog.addEventListener("cancel", (e) => onDialogCancel(ctx, e));
  deps.dialog.addEventListener("close", () => onDialogClose(ctx));
  deps.resetButton.addEventListener("click", () => onReset(ctx));
  renderKeys(ctx);
  return { commandFor: (code) => ctx.state.keyMap.get(code) ?? null };
}
